import type { FC } from "react"
import { Box, Flex, HStack, Icon, Link, Stack, Text } from "@chakra-ui/react"
import { formatUnits } from "ethers/lib/utils.js"
import BigNumber from "bignumber.js"

import { shortenIfAddress } from "web3/helpers/address"
import type { CovalentData, SwapQuote, ExecutableCallSwap } from "query/graphql"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import ArrowRight from "ui/components/icons/ArrowRight"
import { getMainnetReference } from "web3/helpers/chainReference"
import { useDevice } from "common/hooks/useDevice"
import { labelNumber } from "common/helpers/number"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"

type TallyProposalSwapReceiptProps = {
  meta: Pick<
    ExecutableCallSwap,
    "amountIn" | "fee" | "order" | "priceChecker" | "to"
  > & {
    buyToken: {
      data: Omit<CovalentData, "logo">
    }
    sellToken: {
      data: Omit<CovalentData, "logo">
    }
    quote?: Pick<SwapQuote, "buyAmount" | "feeAmount"> | null
  }
  target: string
}

const TallyProposalSwapReceipt: FC<TallyProposalSwapReceiptProps> = ({
  meta,
  target,
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()
  const { amountIn, sellToken, buyToken, quote, fee } = meta

  if (!buyToken || !quote || !sellToken) return null

  // const { address: targetAddress } = parseAccountId(target)

  const { decimals: sellDecimals, symbol: sellSymbol } = sellToken.data
  const { decimals: buyDecimals, symbol: buySymbol } = buyToken.data
  const { buyAmount } = quote
  const formattedAmountIn = labelNumber(
    Number(
      formatUnits(
        new BigNumber(amountIn).plus(fee ?? "0").toFixed(),
        sellDecimals,
      ),
    ),
  )
  const out = new BigNumber(buyAmount).toPrecision(4)
  const formattedOut = labelNumber(
    Number(formatUnits(new BigNumber(out).toString(), buyDecimals)),
  )

  const blockExplorerLink = EXTERNAL_ROUTES.etherscan.address(
    getMainnetReference(),
    target,
  )

  return (
    <>
      <Flex align="center" display={onLargeDevice} p={5} w="full">
        <HStack align="center" spacing={3} w="25%">
          <Icon as={ArrowRight} fill="#344054" h={4} w={4} />
          <Text
            color="#344054"
            fontSize="16px"
            fontWeight={500}
            lineHeight="20px"
          >
            Swap
          </Text>
        </HStack>
        <HStack w="30%">
          <Text textStyle="body.bold.lg">Milkman</Text>
          <Text color="#667085" textStyle="body.regular.lg">
            {`(${shortenIfAddress(target)})`}
          </Text>
          <Link isExternal href={blockExplorerLink}>
            <Icon as={ArrowUpRight} color="#98A2B3" h={2.5} mb={0.5} w={2.5} />
          </Link>
        </HStack>
        <Box borderRadius="md" maxW="45%" px={2} py={1}>
          <Text
            fontWeight="bold"
            overflow="hidden"
            textAlign="center"
            textOverflow="ellipsis"
            textStyle="body.bold.lg"
            whiteSpace="nowrap"
          >
            {`${formattedAmountIn} ${sellSymbol} for ${formattedOut} ${buySymbol}`}
          </Text>
        </Box>
      </Flex>
      <Stack display={onLittleDevice} px={5} py={2} spacing={0}>
        <HStack align="center" py={2}>
          <Icon as={ArrowRight} fill="#344054" h={3} w={3} />
          <Text
            color="#344054"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
          >
            Swap
          </Text>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Address
          </Text>
          <HStack>
            <Stack spacing={0}>
              <Text textStyle="body.bold.md">Milkman</Text>
              <Text color="#667085" textStyle="body.regular.md">
                {`(${shortenIfAddress(target)})`}
              </Text>
            </Stack>
            <Link isExternal href={blockExplorerLink}>
              <Icon
                as={ArrowUpRight}
                color="#98A2B3"
                h={2.5}
                mb={0.5}
                w={2.5}
              />
            </Link>
          </HStack>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Details
          </Text>
          <Box borderRadius="md" px={2} py={1}>
            <Text
              fontWeight="bold"
              overflow="hidden"
              textAlign="center"
              textOverflow="ellipsis"
              textStyle="sm"
              whiteSpace="nowrap"
            >
              {`${formattedAmountIn} ${sellSymbol} for ${formattedOut} ${buySymbol}`}
            </Text>
          </Box>
        </HStack>
      </Stack>
    </>
  )
}

export default TallyProposalSwapReceipt
